import React, { useState } from 'react';
import { LayersIcon, PlusIcon, Trash2Icon, LinkIcon, ImageIcon, ChevronDownIcon, ChevronUpIcon, InfoIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { EDITION_TYPE_DESCRIPTIONS, EDITION_TYPE_LABELS, emberAndAshStory, type EditionType } from '../../../data/linkedEditions';
import { retailerPlatforms } from './constants';

/**
 * Linked editions of ONE parent story.
 *
 * Distinct from the existing "Editions & Formats" section, which covers
 * ebook/paperback/audiobook of identical content. This section is for versions
 * whose CONTENT differs — a lower-violence variant, an alternative ending, a
 * revision — which is why each row carries its own spice, darkness, warnings,
 * ending and "what's different" explanation.
 *
 * Every edition added here stays attached to the parent story rather than
 * becoming an unrelated book record.
 */

interface DraftEdition {
  id: string;
  name: string;
  type: EditionType;
  coverImage: string;
  description: string;
  spiceLevel: number;
  darknessLevel: number;
  endingType: string;
  contentWarnings: string;
  whatsDifferent: string;
  retailers: Array<{
    site: string;
    url: string;
  }>;
  isExpanded: boolean;
}
const ENDING_TYPES = ['HEA', 'HFN', 'Bittersweet', 'Tragic', 'Open', 'Cliffhanger'];

/** Seeded from the shared example so the three areas show the same data. */
const seedFromExample = (): DraftEdition[] => emberAndAshStory.editions.map(edition => ({
  id: edition.id,
  name: edition.name,
  type: edition.type,
  coverImage: edition.coverImage,
  description: edition.description,
  spiceLevel: edition.spiceLevel,
  darknessLevel: edition.darknessLevel,
  endingType: edition.endingType,
  contentWarnings: edition.contentWarnings.join(', '),
  whatsDifferent: edition.whatsDifferent,
  retailers: edition.retailerLinks,
  isExpanded: false
}));
export const LinkedEditionsSection: React.FC = () => {
  const dk = useDarkClasses();
  const [editions, setEditions] = useState<DraftEdition[]>(seedFromExample);
  const update = (id: string, patch: Partial<DraftEdition>) => setEditions(prev => prev.map(edition => edition.id === id ? {
    ...edition,
    ...patch
  } : edition));
  const addEdition = () => setEditions(prev => [...prev, {
    id: `edition-${Date.now()}`,
    name: '',
    type: 'content-variant',
    coverImage: '',
    description: '',
    spiceLevel: 3,
    darknessLevel: 2,
    endingType: 'HEA',
    contentWarnings: '',
    whatsDifferent: '',
    retailers: [],
    isExpanded: true
  }]);
  const removeEdition = (id: string) => setEditions(prev => prev.filter(edition => edition.id !== id));
  const addRetailer = (id: string) => {
    const edition = editions.find(candidate => candidate.id === id);
    if (!edition) return;
    update(id, {
      retailers: [...edition.retailers, {
        site: '',
        url: ''
      }]
    });
  };
  const updateRetailer = (id: string, index: number, patch: Partial<{
    site: string;
    url: string;
  }>) => {
    const edition = editions.find(candidate => candidate.id === id);
    if (!edition) return;
    update(id, {
      retailers: edition.retailers.map((retailer, i) => i === index ? {
        ...retailer,
        ...patch
      } : retailer)
    });
  };
  const removeRetailer = (id: string, index: number) => {
    const edition = editions.find(candidate => candidate.id === id);
    if (!edition) return;
    update(id, {
      retailers: edition.retailers.filter((_, i) => i !== index)
    });
  };
  const fieldClass = `w-full px-3 py-2 rounded-lg border text-sm ${dk.input}`;
  const labelClass = `block text-xs font-medium mb-1.5 ${dk.textSecondary}`;
  return <div className={`p-6 rounded-xl shadow-sm ${dk.card}`} data-id="element-20788" data-mp="y6b2ipl2jxeb">
      <div className="flex items-center justify-between mb-2" data-id="element-20789" data-mp="u5hhq52xaypn">
        <h2 className={`text-lg font-bold flex items-center ${dk.textHeading}`} data-id="element-20790" data-mp="u72d1wx2alok">
          <LayersIcon className="w-5 h-5 mr-2 text-purple-500" data-id="element-20791" data-mp="2nn6maipqa94z" />
          Linked Editions
        </h2>
        <span className={`text-xs ${dk.textMuted}`} data-id="element-20792" data-mp="2sm03om84ykaf">
          {editions.length} linked
        </span>
      </div>

      <p className={`flex gap-2 text-xs mb-5 ${dk.textMuted}`} data-id="element-20793" data-mp="2xejejbffv1bj">
        <InfoIcon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" data-id="element-20794" data-mp="32ddzok0vp1yt" />
        For versions of this story whose content differs — reduced violence, an
        alternative ending, or a revision. All editions stay linked to this one
        story, so readers see a single listing with a choice inside it. For
        ebook, paperback and audiobook of identical content, use Editions &amp;
        Formats instead.
      </p>

      <div className="space-y-4" data-id="element-20795" data-mp="2ipwrsonhf7aa">
        {editions.map(edition => <div key={edition.id} className={`border rounded-xl overflow-hidden ${dk.border} ${dk.surface}`} data-id="element-20796" data-mp="3q38akw1n0ggf">
            <div className={`flex items-center justify-between p-4 cursor-pointer transition-colors hover:bg-black/5 ${edition.isExpanded ? `border-b ${dk.border}` : ''}`} onClick={() => update(edition.id, {
          isExpanded: !edition.isExpanded
        })} data-id="element-20797" data-mp="3u2h35xkxy8ae">
              <div className="flex items-center gap-3 min-w-0" data-id="element-20798" data-mp="19n26y0igrkk1">
                {edition.coverImage ? <img src={edition.coverImage} alt="" className="w-8 h-11 rounded object-cover flex-shrink-0" data-id="element-20799" data-mp="14p0lj8xkf95e" /> : <div className={`w-8 h-11 rounded flex items-center justify-center flex-shrink-0 ${dk.surfaceMuted}`} data-id="element-20800" data-mp="utgtwyq1awmg">
                    <ImageIcon className={`w-3.5 h-3.5 ${dk.textMuted}`} data-id="element-20801" data-mp="lrpoluzeort4" />
                  </div>}
                <div className="min-w-0" data-id="element-20802" data-mp="2l3a0xdjtxnk3">
                  <h3 className={`font-semibold text-sm ${dk.textHeading}`} data-id="element-20803" data-mp="2g4pag5udqi8u">
                    {edition.name || 'Untitled edition'}
                  </h3>
                  <p className={`text-xs mt-0.5 ${dk.textMuted}`} data-id="element-20804" data-mp="2z7r64mly509q">
                    {EDITION_TYPE_LABELS[edition.type]} · Spice{' '}
                    {edition.spiceLevel} · Darkness {edition.darknessLevel} ·{' '}
                    {edition.endingType}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0" data-id="element-20805" data-mp="2v5mf7vmvuhqs">
                <button type="button" onClick={event => {
              event.stopPropagation();
              removeEdition(edition.id);
            }} aria-label={`Remove ${edition.name || 'edition'}`} className={`p-1.5 rounded-md text-red-500 hover:bg-red-500/10`} data-id="element-20806" data-mp="3iymefkog4u32">
                  <Trash2Icon className="w-4 h-4" data-id="element-20807" data-mp="1xjozu73susj2" />
                </button>
                {edition.isExpanded ? <ChevronUpIcon className={`w-4 h-4 ${dk.textMuted}`} data-id="element-20808" data-mp="22juk327qpcr4" /> : <ChevronDownIcon className={`w-4 h-4 ${dk.textMuted}`} data-id="element-20809" data-mp="1uy7ir8zuohyq" />}
              </div>
            </div>

            {edition.isExpanded && <div className="p-4 space-y-4" data-id="element-20810" data-mp="23zyqskhv50vr">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-20811" data-mp="23zoxoo1iudlu">
                  <div data-id="element-20812" data-mp="1z26wxkxnr4ox">
                    <label className={labelClass} data-id="element-20813" data-mp="1z1nd1yog6689">Edition name</label>
                    <input type="text" value={edition.name} onChange={e => update(edition.id, {
                name: e.target.value
              })} placeholder="e.g. Light Edition" className={fieldClass} data-id="element-20814" data-mp="1barmt5zn3j34" />
                  </div>
                  <div data-id="element-20815" data-mp="skjrha26lwxh">
                    <label className={labelClass} data-id="element-20816" data-mp="sk0apwo9pel1">Edition type</label>
                    <select value={edition.type} onChange={e => update(edition.id, {
                type: e.target.value as EditionType
              })} className={fieldClass} data-id="element-20817" data-mp="nm8ewrki58bk">
                      {(Object.keys(EDITION_TYPE_LABELS) as EditionType[]).map(type => <option key={type} value={type} data-id="element-20818" data-mp="gxjywzgkbrhe">
                          {EDITION_TYPE_LABELS[type]}
                        </option>)}
                    </select>
                    <p className={`text-xs mt-1 ${dk.textMuted}`} data-id="element-20819" data-mp="py8azomebhrn">
                      {EDITION_TYPE_DESCRIPTIONS[edition.type]}
                    </p>
                  </div>
                </div>

                <div data-id="element-20820" data-mp="zs8mq9c6nl9c">
                  <label className={labelClass} data-id="element-20821" data-mp="14pqwu31xs6ix">Edition cover image URL</label>
                  <input type="text" value={edition.coverImage} onChange={e => update(edition.id, {
              coverImage: e.target.value
            })} placeholder="https://…" className={fieldClass} data-id="element-20822" data-mp="14no2x7xnxt8x" />
                </div>

                <div data-id="element-20823" data-mp="2k8ymn8gtdesh">
                  <label className={labelClass} data-id="element-20824" data-mp="2kaj9sd0stvgb">Edition description</label>
                  <textarea rows={3} value={edition.description} onChange={e => update(edition.id, {
              description: e.target.value
            })} placeholder="How this edition is presented to readers…" className={`${fieldClass} resize-none`} data-id="element-20825" data-mp="2fawuzz5bo75e" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-20826" data-mp="2y654n0tpr9gn">
                  <div data-id="element-20827" data-mp="3vodiua1782mj">
                    <label className={labelClass} data-id="element-20828" data-mp="3vpon0ayk3ce6">Spice level</label>
                    <select value={edition.spiceLevel} onChange={e => update(edition.id, {
                spiceLevel: Number(e.target.value)
              })} className={fieldClass} data-id="element-20829" data-mp="3qw3li8xr80a8">
                      {[1, 2, 3, 4, 5].map(level => <option key={level} value={level} data-id="element-20830" data-mp="11k1jd86sgegx">
                          {level}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-20831" data-mp="1bi7kvooonil9">
                    <label className={labelClass} data-id="element-20832" data-mp="1bgwfxlji44sk">Darkness level</label>
                    <select value={edition.darknessLevel} onChange={e => update(edition.id, {
                darknessLevel: Number(e.target.value)
              })} className={fieldClass} data-id="element-20833" data-mp="1p92ksbh7epcp">
                      {[1, 2, 3, 4, 5].map(level => <option key={level} value={level} data-id="element-20834" data-mp="2dvs4e1h5fmoc">
                          {level}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-20835" data-mp="23wkr1eaezs18">
                    <label className={labelClass} data-id="element-20836" data-mp="23z71pmslqnno">Ending</label>
                    <select value={edition.endingType} onChange={e => update(edition.id, {
                endingType: e.target.value
              })} className={fieldClass} data-id="element-20837" data-mp="28uw19q4mg4jz">
                      {ENDING_TYPES.map(ending => <option key={ending} value={ending} data-id="element-20838" data-mp="1l4rx9hniu2sg">
                          {ending}
                        </option>)}
                    </select>
                  </div>
                </div>

                <div data-id="element-20839" data-mp="1g81jj0z6vja7">
                  <label className={labelClass} data-id="element-20840" data-mp="1g6gq5eom26dx">
                    Content warnings (comma separated)
                  </label>
                  <input type="text" value={edition.contentWarnings} onChange={e => update(edition.id, {
              contentWarnings: e.target.value
            })} placeholder="Graphic Violence, Grief, Character Death" className={fieldClass} data-id="element-20841" data-mp="x47vdi9bnjn9" />
                  <p className={`text-xs mt-1 ${dk.textMuted}`} data-id="element-20842" data-mp="1g190d32vljr7">
                    Readers filter on these per edition, so a lower-intensity
                    edition can be discovered when a stricter one is excluded.
                  </p>
                </div>

                <div data-id="element-20843" data-mp="11504mbi71ojj">
                  <label className={labelClass} data-id="element-20844" data-mp="113ozo8d0iaqu">
                    What differs from the original?
                  </label>
                  <textarea rows={2} value={edition.whatsDifferent} onChange={e => update(edition.id, {
              whatsDifferent: e.target.value
            })} placeholder="Shown to readers before they buy, so nobody picks the wrong version." className={`${fieldClass} resize-none`} data-id="element-20845" data-mp="3qv3odxp7c461" />
                </div>

                {/* Edition-specific retailer links */}
                <div data-id="element-20846" data-mp="2g3nwxozq25x6">
                  <div className="flex items-center justify-between mb-2" data-id="element-20847" data-mp="2l0nzhpxd4qjj">
                    <label className={labelClass + ' mb-0'} data-id="element-20848" data-mp="2l2qp48ulshnq">
                      Retailer links for this edition
                    </label>
                    <button type="button" onClick={() => addRetailer(edition.id)} className="inline-flex items-center gap-1 text-xs font-medium text-purple-500 hover:text-purple-400" data-id="element-20849" data-mp="2u12ph2l9uttd">
                      <PlusIcon className="w-3.5 h-3.5" data-id="element-20850" data-mp="14medv3ki9fwe" />
                      Add link
                    </button>
                  </div>
                  {edition.retailers.length === 0 ? <p className={`text-xs ${dk.textMuted}`} data-id="element-20851" data-mp="2q4f4aa2k88is">
                      No retailer links yet for this edition.
                    </p> : <div className="space-y-2" data-id="element-20852" data-mp="343esjgm5ulef">
                      {edition.retailers.map((retailer, index) => <div key={index} className="flex gap-2" data-id="element-20853" data-mp="300rwt4mdcgby" data-mpi={index}>
                          <select value={retailer.site} onChange={e => updateRetailer(edition.id, index, {
                  site: e.target.value
                })} className={`${fieldClass} sm:w-44`} data-id="element-20854" data-mp="2cdjrimoxx79x">
                            <option value="" data-id="element-20855" data-mp="1snhsoxcsah2f">Select retailer</option>
                            {retailerPlatforms.map((platform: any) => {
                    const label = typeof platform === 'string' ? platform : platform.label || platform.name;
                    return <option key={label} value={label} data-id="element-20856" data-mp="14mw6ah7c1tsp">
                                  {label}
                                </option>;
                  })}
                          </select>
                          <div className="relative flex-1" data-id="element-20857" data-mp="228tjq06uv8xs">
                            <LinkIcon className={`absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${dk.textMuted}`} data-id="element-20858" data-mp="228bq7bbkv8tk" />
                            <input type="text" value={retailer.url} onChange={e => updateRetailer(edition.id, index, {
                    url: e.target.value
                  })} placeholder="https://…" className={`${fieldClass} pl-9`} data-id="element-20859" data-mp="1s9mbtsiw46i2" />
                          </div>
                          <button type="button" onClick={() => removeRetailer(edition.id, index)} aria-label="Remove retailer link" className="p-2 rounded-md text-red-500 hover:bg-red-500/10" data-id="element-20860" data-mp="24tj8bwci81w2">
                            <Trash2Icon className="w-4 h-4" data-id="element-20861" data-mp="1qsyqb2eeya3m" />
                          </button>
                        </div>)}
                    </div>}
                </div>
              </div>}
          </div>)}
      </div>

      <button type="button" onClick={addEdition} className={`w-full mt-4 py-3 rounded-xl border-2 border-dashed text-sm font-medium transition-colors flex items-center justify-center gap-2 ${dk.isDark ? 'border-gray-700 text-gray-400 hover:border-purple-500 hover:text-purple-400' : 'border-gray-300 text-gray-500 hover:border-purple-500 hover:text-purple-600'}`} data-id="element-20862" data-mp="3g86j2fx0a8dk">
        <PlusIcon className="w-4 h-4" data-id="element-20863" data-mp="6bfz8anb3zng" />
        Add another edition
      </button>
    </div>;
};
export default LinkedEditionsSection;